// src/utils/fileAccess.js
//
// Phase 0B secure file access helpers. Replaces direct
// `href={`${API_URL}/uploads/...`}` / `src={...}` / `window.open(...)` links
// (which never carried the Authorization header and hit the now-deprecated
// public `/uploads` static mount) with authenticated Blob fetches through
// the shared `api` client.
//
// Browser-native navigation (a plain <a href>, window.open, or an <img src>)
// cannot attach a custom Authorization header, so the only reliable way to
// fetch a permission-checked file is to request it as a Blob through axios
// (which does attach the header via api.js's interceptor) and then hand the
// browser a short-lived local blob: URL instead of the real API URL. The
// real JWT never appears in any URL, browser history, or address bar.
import api from "../api";
import apiPublic from "../apiPublic";

/**
 * Opens a file inline (e.g. in a new tab, or for embedding in an <img>/
 * <object>) by fetching it as an authenticated Blob and handing back a
 * revocable object URL. The caller is responsible for calling the returned
 * `revoke()` when done with it (e.g. on modal close / component unmount) —
 * forgetting to revoke just leaks a small amount of memory, it is not a
 * security issue, but should still be done.
 *
 * @param {string} url - relative API path, e.g. `/files/allocation-documents/12/view`
 * @returns {Promise<{ objectUrl: string, blob: Blob, revoke: () => void }>}
 */
export async function viewAuthenticatedFile(url) {
  const response = await api.get(url, { responseType: "blob" });
  const blob = response.data;
  const objectUrl = window.URL.createObjectURL(blob);
  return {
    objectUrl,
    blob,
    revoke: () => window.URL.revokeObjectURL(objectUrl),
  };
}

/**
 * Fetches a file as an authenticated Blob and triggers a browser download
 * with the correct file name (browsers ignore the server's suggested name
 * for blob: URLs unless we set it explicitly via a temporary <a download>).
 * Automatically revokes its own temporary object URL after the download is
 * triggered.
 *
 * @param {string} url - relative API path, e.g. `/files/product-invoices/7/download`
 * @param {string} fileName - the file name to save as
 */
export async function downloadAuthenticatedFile(url, fileName) {
  const response = await api.get(url, { responseType: "blob" });
  const blob = response.data;
  const objectUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Give the browser a moment to actually start the download before revoking.
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Convenience wrapper: fetches the file and opens it in a new tab (useful
 * for PDFs/images where the browser has a native inline viewer). Falls back
 * to a same-tab navigation if the popup was blocked, and always revokes the
 * object URL once the new tab has had a chance to load it.
 *
 * @param {string} url - relative API path
 */
export async function viewAuthenticatedFileInNewTab(url) {
  const { objectUrl, revoke } = await viewAuthenticatedFile(url);
  const win = window.open(objectUrl, "_blank");
  if (!win) {
    // Popup blocked — fall back to a same-tab navigation rather than silently failing.
    window.location.assign(objectUrl);
  }
  // Revoke after a delay long enough for the new tab/context to load the blob.
  window.setTimeout(revoke, 30000);
}

/**
 * Phase 16 (Public Form Links) counterpart to viewAuthenticatedFileInNewTab —
 * same Blob-fetch-then-object-URL approach (so the token stays inside an
 * XHR request rather than a plain page navigation/address-bar URL), just
 * through apiPublic instead of the authenticated `api` client. `url` is the
 * full `/public/form-access/:token/files/:fileId/view`-shaped path; the
 * token is already part of it (it IS this endpoint's credential), so there
 * is no separate Authorization header to attach here.
 *
 * @param {string} url - relative API path
 */
export async function viewPublicFileInNewTab(url) {
  const response = await apiPublic.get(url, { responseType: "blob" });
  const objectUrl = window.URL.createObjectURL(response.data);
  const win = window.open(objectUrl, "_blank");
  if (!win) {
    window.location.assign(objectUrl);
  }
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 30000);
}

/**
 * Extracts a user-facing error message from a failed authenticated file
 * request, matching the shape used elsewhere in the app
 * (err?.response?.data?.message / .error), with sensible fallbacks for the
 * specific statuses this endpoint family can return.
 */
export function getFileAccessErrorMessage(err, fileLabel = "file") {
  const status = err?.response?.status;
  // Status-specific copy takes priority over the server's generic message so
  // callers never show the same "permission" wording for every failure kind
  // (401 vs 403 vs 404 vs 500 are genuinely different situations for the user).
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return `You do not have permission to view this ${fileLabel}.`;
  if (status === 404) return `${fileLabel[0].toUpperCase()}${fileLabel.slice(1)} was not found.`;
  if (status === 500) return `Unable to load ${fileLabel}.`;
  const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
  return serverMessage || "Failed to access file. Please try again.";
}
